import { Sprite } from "@pixi/react"
import type { FederatedPointerEvent } from "pixi.js"
import { type ComponentType, memo, useCallback, useState } from "react"
import { ICON_COLORS } from "@/domains/visualization/constants/graph"
import { useIconTexture } from "@/domains/visualization/hooks/useIconTexture/useIconTexture"
import type { IconColors } from "@/domains/visualization/types/Graph"

interface NodeIconTooltipProps {
  FragmentsIconComponent: ComponentType<{
    className?: string
    style?: React.CSSProperties
    color?: string
    primaryColor?: string
    secondaryColor?: string
  }>
  onIconClick?: () => void
  x?: number
  y?: number
  visible?: boolean
  onTooltipHover?: () => void
  onTooltipLeave?: () => void
  isActive?: boolean
  size?: number
  colors?: IconColors
}

export const NodeIconTooltip = memo(function NodeIconTooltip({
  FragmentsIconComponent,
  onIconClick,
  x = 0,
  y = 0,
  visible = true,
  onTooltipHover,
  onTooltipLeave,
  isActive = true,
  size,
  colors
}: NodeIconTooltipProps) {
  const [isHovered, setIsHovered] = useState(false)

  const hoverColors = {
    primaryColor: "#76c7b2",
    secondaryColor: ICON_COLORS.SECONDARY
  }

  const finalColors =
    isHovered && !isActive
      ? hoverColors
      : {
          primaryColor: colors?.primaryColor,
          secondaryColor: colors?.secondaryColor
        }

  const { texture, scale } = useIconTexture({
    isActive: isActive || isHovered,
    size,
    FragmentsIconComponent,
    colors: finalColors
  })

  const handleClick = useCallback(
    (event: FederatedPointerEvent) => {
      event.stopPropagation()
      onIconClick?.()
    },
    [onIconClick]
  )

  const handlePointerOver = useCallback(() => {
    setIsHovered(true)
    onTooltipHover?.()
  }, [onTooltipHover])

  const handlePointerOut = useCallback(() => {
    setIsHovered(false)
    onTooltipLeave?.()
  }, [onTooltipLeave])

  if (!visible || !texture) {
    return null
  }

  return (
    <Sprite
      texture={texture}
      scale={scale}
      x={x}
      y={y}
      anchor={0.5}
      eventMode="static"
      pointerdown={handleClick}
      pointerover={handlePointerOver}
      pointerout={handlePointerOut}
      cursor={onIconClick ? "pointer" : "default"}
    />
  )
})
